import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  CheckCircle2,
  Circle,
  AlertCircle,
  Flag,
  Briefcase,
  Users,
  Activity,
  Plus,
  Edit3,
  Trash2,
  ExternalLink,
  Kanban,
  List,
  Search,
  RefreshCw,
  ChevronRight,
  X,
  AlertOctagon,
  Sparkles,
  Receipt,
  Share2,
  Check,
  Send,
  DollarSign,
} from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { useToast } from '@/context/ToastContext.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Skeleton, SkeletonCard } from '@/components/ui/skeleton.js';
import { PlatformBadge } from '@/components/ui/PlatformBadge.js';
import { DeliveryCountdownBadge } from '@/components/ui/DeliveryCountdownBadge.js';
import { DeleteConfirmModal } from '@/components/ui/DeleteConfirmModal.js';
import { formatExternalUrl } from '@/lib/utils.js';
import { Project, Task, Milestone, MilestoneStatus, PlatformType, TaskStatus, TaskPriority, Invoice } from '@/types/index.js';
import { ChannelDeliveryModal } from '@/components/platform/ChannelDeliveryModal.js';
import { TOSComplianceGuard } from '@/components/platform/TOSComplianceGuard.js';
import { calculatePlatformCut, calculateEffectiveHourlyYield } from '@/lib/platformFees.js';

function formatDateSafe(dateString?: string | null): string {
  if (!dateString) return 'No Deadline';
  const trimmed = dateString.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  const d = new Date(dateString);
  return isNaN(d.getTime())
    ? dateString
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const role = user?.role || 'team_member';
  const isOwner = role === 'owner';
  const canManage = isOwner || role === 'manager';
  const isViewer = role === 'viewer';

  // Core Data States
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Active Tab
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'team_activity' | 'blockers' | 'invoices'>('overview');
  const [projectBlockers, setProjectBlockers] = useState<any[]>([]);
  const [projectInvoices, setProjectInvoices] = useState<Invoice[]>([]);

  // Task View Mode (Kanban vs List)
  const [taskViewMode, setTaskViewMode] = useState<'kanban' | 'list'>('kanban');
  const [taskSearch, setTaskSearch] = useState('');
  const [taskStatusFilter, setTaskStatusFilter] = useState('all');
  const [taskMilestoneFilter, setTaskMilestoneFilter] = useState('all');

  // Modals
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showAddMilestoneModal, setShowAddMilestoneModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);
  const [deleteMilestoneTarget, setDeleteMilestoneTarget] = useState<Milestone | null>(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);

  // Form States
  const [editProjectForm, setEditProjectForm] = useState<any>({});
  const [newTaskForm, setNewTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium' as TaskPriority,
    assignee_id: '',
    milestone_id: '',
    due_date: '',
    estimated_hours: 0,
  });
  const [milestoneForm, setMilestoneForm] = useState({
    title: '',
    description: '',
    due_date: '',
    status: 'pending' as MilestoneStatus,
    order: 1,
  });

  // Fetch all project details dynamically
  const fetchProjectData = async (isManualRefresh = false) => {
    if (!id) return;
    if (isManualRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      // 1. Fetch Project
      const prjRes = await api.get<{ data: Project }>(`/api/v1/projects/${encodeURIComponent(id)}`);
      const prjData = prjRes?.data || prjRes;
      if (!prjData || !prjData.id) {
        throw new Error('Project not found');
      }

      setProject(prjData);

      // Populate edit form defaults
      setEditProjectForm({
        name: prjData.name || '',
        description: prjData.description || '',
        category: prjData.category || 'WordPress',
        client_name: prjData.client_name || '',
        client_username: prjData.client_username || '',
        platform: (prjData.platform || 'direct') as PlatformType,
        platform_url: prjData.platform_url || '',
        platform_order_id: prjData.platform_order_id || '',
        status: prjData.status || 'active',
        priority: prjData.priority || 'medium',
        progress_percent: prjData.progress_percent ?? 0,
        progress_mode: prjData.progress_mode || 'automatic',
        manual_override_reason: prjData.manual_override_reason || '',
        deadline: prjData.deadline ? prjData.deadline.substring(0, 10) : '',
        budget: (prjData as any).budget || '',
        currency: (prjData as any).currency || 'PKR',
      });

      // 2. Fetch Tasks, Milestones, Activity, Team, and Blockers concurrently
      const [tasksRes, milestonesRes, activityRes, teamRes, blockersRes] = await Promise.allSettled([
        api.get<{ data: Task[] }>(`/api/v1/tasks?project_id=${encodeURIComponent(prjData.id)}`),
        api.get<{ data: Milestone[] }>(`/api/v1/milestones?project_id=${encodeURIComponent(prjData.id)}`),
        api.get<{ data: any[] }>(`/api/v1/activity?project_id=${encodeURIComponent(prjData.id)}`),
        api.get<{ data: any[] }>('/api/v1/team'),
        api.get<{ data: any[] }>(`/api/v1/blockers?project_id=${encodeURIComponent(prjData.id)}`),
      ]);

      // Resolve Tasks
      if (tasksRes.status === 'fulfilled') {
        const rawTasks = tasksRes.value?.data || (Array.isArray(tasksRes.value) ? tasksRes.value : []);
        const filteredTasks = rawTasks.filter(
          (t: Task) => t.project_id === prjData.id || t.project_id === prjData.slug
        );
        setTasks(filteredTasks);
      }

      // Resolve Milestones
      if (milestonesRes.status === 'fulfilled') {
        const rawMilestones = milestonesRes.value?.data || (Array.isArray(milestonesRes.value) ? milestonesRes.value : []);
        const filteredMilestones = rawMilestones.filter(
          (m: Milestone) => m.project_id === prjData.id || m.project_id === prjData.slug
        );
        setMilestones(filteredMilestones.length > 0 ? filteredMilestones : (prjData.milestones || []));
      } else {
        setMilestones(prjData.milestones || []);
      }

      // Resolve Activity Logs
      if (activityRes.status === 'fulfilled') {
        const rawLogs = activityRes.value?.data || (Array.isArray(activityRes.value) ? activityRes.value : []);
        setActivityLogs(rawLogs);
      }

      // Resolve Team Members
      if (teamRes.status === 'fulfilled') {
        const rawTeam = teamRes.value?.data || (Array.isArray(teamRes.value) ? teamRes.value : []);
        setTeamMembers(rawTeam);
      }

      // Resolve Project Blockers
      if (blockersRes.status === 'fulfilled') {
        const rawBlockers = blockersRes.value?.data || (Array.isArray(blockersRes.value) ? blockersRes.value : []);
        setProjectBlockers(rawBlockers);
      }

      // Fetch linked invoices for owner
      if (isOwner) {
        api.get<{ data: Invoice[] } | Invoice[]>(`/api/invoices?project_id=${encodeURIComponent(prjData.id)}`)
          .then((res: any) => {
            const list = res?.data || (Array.isArray(res) ? res : []);
            setProjectInvoices(list);
          })
          .catch((err) => {
            console.error('Failed to load project invoices:', err);
            setProjectInvoices([]);
          });
      }

      if (isManualRefresh) {
        toast.success('Project details refreshed');
      }
    } catch (err: any) {
      console.error('Failed to fetch project details:', err);
      setError(err?.message || 'Project not found');
      setProject(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, [id]);

  // Derived KPI Stats
  const totalTasksCount = tasks.length;
  const completedTasksCount = tasks.filter((t) => ['done', 'completed'].includes((t.status || '').toLowerCase())).length;
  const openTasksCount = totalTasksCount - completedTasksCount;
  const totalMilestonesCount = milestones.length;
  const completedMilestonesCount = milestones.filter((m) => m.status === 'completed').length;
  const pendingMilestonesCount = totalMilestonesCount - completedMilestonesCount;
  const recordedEffortHours = useMemo(() => {
    const sum = tasks.reduce((acc, t) => acc + (Number(t.recorded_hours) || 0), 0);
    if (sum > 0) return Math.round(sum * 10) / 10;
    if (project?.recorded_hours !== undefined && project.recorded_hours !== null) {
      return Number(project.recorded_hours);
    }
    return 0;
  }, [project, tasks]);

  const computedProgressPercent = useMemo(() => {
    if (!project) return 0;
    if (project.progress_mode === 'manual') {
      return Math.min(100, Math.max(0, project.progress_percent ?? 0));
    }
    if (totalTasksCount === 0) {
      return Math.min(100, Math.max(0, project.progress_percent ?? 0));
    }
    return Math.min(100, Math.max(0, Math.round((completedTasksCount / totalTasksCount) * 100)));
  }, [project, totalTasksCount, completedTasksCount]);

  // Stably sorted milestones by order then due date
  const sortedMilestones = useMemo(() => {
    return [...milestones].sort((a, b) => {
      const orderA = Number(a.order ?? (a as any).sort_order ?? 0);
      const orderB = Number(b.order ?? (b as any).sort_order ?? 0);
      if (orderA !== orderB) return orderA - orderB;
      const dateA = a.due_date || '';
      const dateB = b.due_date || '';
      if (!dateA && dateB) return 1;
      if (dateA && !dateB) return -1;
      if (dateA && dateB && dateA !== dateB) return dateA.localeCompare(dateB);
      return (a.created_at || '').localeCompare(b.created_at || '');
    });
  }, [milestones]);

  // Assigned Team Members working on this project (including multi-assignees & manager)
  const assignedTeam = useMemo(() => {
    const userIds = new Set<string>();
    const names = new Set<string>();

    if ((project as any)?.manager_id) {
      userIds.add((project as any).manager_id);
    }

    tasks.forEach((t) => {
      if (t.assignee_id) userIds.add(t.assignee_id);
      if (t.assignee_name) {
        const parts = t.assignee_name.split(/[,+&/]/).map((s) => s.trim().toLowerCase()).filter(Boolean);
        parts.forEach((p) => names.add(p));
        names.add(t.assignee_name.trim().toLowerCase());
      }
    });

    return teamMembers.filter((m) => {
      if (userIds.has(m.id)) return true;
      const mName = (m.name || '').toLowerCase().trim();
      if (!mName) return false;
      if (names.has(mName)) return true;
      for (const n of names) {
        if (n.includes(mName) || mName.includes(n)) return true;
      }
      return false;
    });
  }, [project, tasks, teamMembers]);

  const canApproveDone = canManage;
  const canEditTask = (t: Task) => {
    if (isViewer) return false;
    if (canManage) return true;
    if (t.assignee_id && user?.id && t.assignee_id === user.id) return true;
    if (t.assignee_name && user?.name && t.assignee_name.toLowerCase().includes(user.name.toLowerCase())) return true;
    return false;
  };

  // Filtered Tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (taskStatusFilter !== 'all' && t.status !== taskStatusFilter) return false;
      if (taskMilestoneFilter !== 'all') {
        if (taskMilestoneFilter === 'unlinked') {
          if (t.milestone_id) return false;
        } else if (t.milestone_id !== taskMilestoneFilter && String(t.milestone_id) !== String(taskMilestoneFilter)) {
          return false;
        }
      }
      if (taskSearch.trim()) {
        const q = taskSearch.toLowerCase();
        const matchesTitle = (t.title || '').toLowerCase().includes(q);
        const matchesDesc = (t.description || '').toLowerCase().includes(q);
        const matchesAssignee = (t.assignee_name || '').toLowerCase().includes(q);
        if (!matchesTitle && !matchesDesc && !matchesAssignee) return false;
      }
      return true;
    });
  }, [tasks, taskStatusFilter, taskMilestoneFilter, taskSearch]);

  // Kanban Tasks by column
  const kanbanColumns: { status: TaskStatus; label: string; badgeVariant: any }[] = [
    { status: 'todo', label: 'To Do', badgeVariant: 'todo' },
    { status: 'in_progress', label: 'In Progress', badgeVariant: 'in_progress' },
    { status: 'in_review', label: 'In Review', badgeVariant: 'in_review' },
    { status: 'blocked', label: 'Blocked', badgeVariant: 'blocked' },
    { status: 'done', label: 'Done', badgeVariant: 'done' },
  ];

  // Actions: Update Task Status
  const handleUpdateTaskStatus = async (taskId: string, nextStatus: TaskStatus) => {
    if (isViewer) return;
    const prevTasks = [...tasks];
    setTasks((current) =>
      current.map((t) =>
        t.id === taskId
          ? {
              ...t,
              status: nextStatus,
              progress_percent: nextStatus === 'done' ? 100 : t.progress_percent,
            }
          : t
      )
    );

    try {
      await api.patch(`/api/v1/tasks/${taskId}`, {
        status: nextStatus,
        progress_percent: nextStatus === 'done' ? 100 : undefined,
      });
      toast.success(`Task moved to ${nextStatus.replace('_', ' ')}`);
      // Background re-fetch to keep progress calculation synced
      if (id) {
        api.get<{ data: Project }>(`/api/v1/projects/${encodeURIComponent(id)}`).then((res) => {
          if (res?.data) setProject(res.data);
        });
      }
    } catch (err: any) {
      setTasks(prevTasks);
      toast.error(err?.message || 'Failed to update task status');
    }
  };

  // Actions: Create Task in Project
  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project || !newTaskForm.title.trim() || isViewer) return;

    try {
      const payload: any = {
        project_id: project.id,
        project_name: project.name,
        title: newTaskForm.title.trim(),
        description: newTaskForm.description.trim() || null,
        priority: newTaskForm.priority,
        status: 'todo',
        due_date: newTaskForm.due_date || null,
        estimated_hours: Number(newTaskForm.estimated_hours) || null,
      };

      if (newTaskForm.assignee_id) {
        payload.assignee_id = newTaskForm.assignee_id;
        const assignedUser = teamMembers.find((m) => m.id === newTaskForm.assignee_id);
        if (assignedUser) payload.assignee_name = assignedUser.name;
      }
      if (newTaskForm.milestone_id) {
        payload.milestone_id = newTaskForm.milestone_id;
      }

      const res = await api.post('/api/v1/tasks', payload);
      const createdTask = res?.data || res;
      setTasks((prev) => [createdTask, ...prev]);
      setShowAddTaskModal(false);
      setNewTaskForm({
        title: '',
        description: '',
        priority: 'medium',
        assignee_id: '',
        milestone_id: '',
        due_date: '',
        estimated_hours: 0,
      });
      toast.success('Task created successfully');

      // Refresh project to update total_tasks & progress
      fetchProjectData(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create task');
    }
  };

  // Actions: Cycle Milestone Status
  const handleToggleMilestoneStatus = async (m: Milestone) => {
    if (!canManage) return;
    const nextStatus: MilestoneStatus =
      m.status === 'pending'
        ? 'in_progress'
        : m.status === 'in_progress'
        ? 'completed'
        : 'pending';

    const prevMilestones = [...milestones];
    setMilestones((current) =>
      current.map((item) => (item.id === m.id ? { ...item, status: nextStatus } : item))
    );

    try {
      await api.put(`/api/v1/milestones/${m.id}`, { status: nextStatus });
      toast.success(`Milestone marked as ${nextStatus.replace('_', ' ')}`);
    } catch (err: any) {
      setMilestones(prevMilestones);
      toast.error(err?.message || 'Failed to update milestone status');
    }
  };

  // Actions: Create Milestone
  const handleCreateMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project || !milestoneForm.title.trim() || !canManage) return;

    try {
      const res = await api.post('/api/v1/milestones', {
        project_id: project.id,
        title: milestoneForm.title.trim(),
        description: milestoneForm.description.trim() || null,
        due_date: milestoneForm.due_date || null,
        status: milestoneForm.status,
        order: Number(milestoneForm.order) || milestones.length + 1,
      });
      const created = res?.data || res;
      setMilestones((prev) => [...prev, created]);
      setShowAddMilestoneModal(false);
      setMilestoneForm({ title: '', description: '', due_date: '', status: 'pending', order: 1 });
      toast.success('Milestone added successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to add milestone');
    }
  };

  // Actions: Save Edit Milestone
  const handleSaveEditMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMilestone || !milestoneForm.title.trim() || !canManage) return;

    try {
      await api.put(`/api/v1/milestones/${editingMilestone.id}`, {
        title: milestoneForm.title.trim(),
        description: milestoneForm.description.trim() || null,
        due_date: milestoneForm.due_date || null,
        status: milestoneForm.status,
        order: Number(milestoneForm.order) || 0,
      });
      setMilestones((prev) =>
        prev.map((item) =>
          item.id === editingMilestone.id
            ? {
                ...item,
                title: milestoneForm.title.trim(),
                description: milestoneForm.description.trim() || null,
                due_date: milestoneForm.due_date || null,
                status: milestoneForm.status,
              }
            : item
        )
      );
      setEditingMilestone(null);
      toast.success('Milestone updated');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update milestone');
    }
  };

  // Actions: Delete Milestone
  const handleDeleteMilestone = async () => {
    if (!deleteMilestoneTarget || !canManage) return;
    try {
      await api.delete(`/api/v1/milestones/${deleteMilestoneTarget.id}`);
      setMilestones((prev) => prev.filter((m) => m.id !== deleteMilestoneTarget.id));
      setTasks((prev) =>
        prev.map((t) =>
          t.milestone_id === deleteMilestoneTarget.id ? { ...t, milestone_id: null as any } : t
        )
      );
      setDeleteMilestoneTarget(null);
      toast.success('Milestone deleted');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete milestone');
    }
  };

  // Actions: Move or Unlink Task Milestone
  const handleMoveTaskMilestone = async (taskId: string, targetMilestoneId: string | null) => {
    try {
      await api.patch(`/api/v1/tasks/${taskId}`, {
        milestone_id: targetMilestoneId,
      });
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, milestone_id: targetMilestoneId } : t))
      );
      if (targetMilestoneId) {
        const targetM = milestones.find((m) => m.id === targetMilestoneId);
        toast.success(`Task moved to ${targetM?.title || 'milestone'}`);
      } else {
        toast.success('Task unlinked from milestone');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update task milestone');
    }
  };

  // Actions: Save Edit Project
  const handleSaveProjectEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project || !canManage) return;

    try {
      const payload: any = {
        name: editProjectForm.name.trim(),
        description: editProjectForm.description.trim() || null,
        category: editProjectForm.category,
        client_name: editProjectForm.client_name.trim() || null,
        client_username: editProjectForm.client_username.trim() || null,
        platform: editProjectForm.platform,
        platform_url: editProjectForm.platform_url.trim() || null,
        platform_order_id: editProjectForm.platform_order_id.trim() || null,
        status: editProjectForm.status,
        priority: editProjectForm.priority,
        deadline: editProjectForm.deadline || null,
        progress_mode: editProjectForm.progress_mode,
        progress_percent:
          editProjectForm.progress_mode === 'manual'
            ? Number(editProjectForm.progress_percent)
            : undefined,
        manual_override_reason:
          editProjectForm.progress_mode === 'manual'
            ? editProjectForm.manual_override_reason
            : null,
      };

      if (isOwner && editProjectForm.budget !== undefined) {
        payload.budget =
          editProjectForm.budget !== '' && editProjectForm.budget !== null
            ? Number(editProjectForm.budget)
            : null;
        payload.currency = editProjectForm.currency || 'PKR';
      }

      const res = await api.patch(`/api/v1/projects/${project.id}`, payload);
      const updated = res?.data || res;
      setProject(updated);
      setShowEditProjectModal(false);
      toast.success('Project details updated successfully');
      fetchProjectData(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update project');
    }
  };

  // Actions: Delete Project
  const handleDeleteProject = async () => {
    if (!project || !isOwner) return;

    try {
      setShowDeleteProjectModal(false);
      await api.delete(`/api/v1/projects/${project.id}`);
      toast.success(`Project "${project.name}" deleted successfully`);
      navigate('/projects', { replace: true });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete project');
    }
  };

  const handleResolveProjectBlocker = async (b: any) => {
    try {
      await api.patch(`/api/v1/blockers/${b.id}`, {
        status: 'resolved',
        resolution_notes: b.resolution_notes || 'Resolved per AI diagnostic guidance and verified on project staging.',
      });
      toast.success('Blocker resolved successfully!');
      fetchProjectData(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to resolve blocker');
    }
  };

  // Client Guest Portal Token Generation
  const [generatingPortalToken, setGeneratingPortalToken] = useState(false);
  const [portalLinkCopied, setPortalLinkCopied] = useState(false);

  const handleGenerateAndCopyPortalLink = async () => {
    if (!project || !canManage) return;
    setGeneratingPortalToken(true);
    try {
      const res = await api.post<any>(`/api/v1/projects/${project.id}/portal-token`, {});
      const token = res.data?.token || res.token;
      const origin = window.location.origin;
      const pathname = window.location.pathname;
      const fullUrl = `${origin}${pathname}#/portal?token=${token}`;
      await navigator.clipboard.writeText(fullUrl);
      setPortalLinkCopied(true);
      toast.success('Branded Client Portal guest link generated & copied to clipboard! 📋');
      setTimeout(() => setPortalLinkCopied(false), 3000);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate client portal link');
    } finally {
      setGeneratingPortalToken(false);
    }
  };


  // Render: Loading Skeleton
  if (loading) {
    return (
      <div className="space-y-6 max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-pulse">
        {/* Breadcrumb Skeleton */}
        <div className="flex items-center space-x-2">
          <Skeleton className="h-4 w-20" />
          <span className="text-slate-300">/</span>
          <Skeleton className="h-4 w-40" />
        </div>

        {/* Header Skeleton */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-6 bg-white dark:bg-[#12211F] rounded-2xl border border-slate-200 dark:border-brand-border">
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
              <Skeleton className="h-6 w-28 rounded-full" />
            </div>
            <Skeleton className="h-9 w-72" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="flex items-center space-x-3">
            <Skeleton className="h-10 w-28 rounded-lg" />
            <Skeleton className="h-10 w-28 rounded-lg" />
          </div>
        </div>

        {/* KPI Skeleton Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} className="h-28" />
          ))}
        </div>

        {/* Content Tabs Skeleton */}
        <div className="h-96 bg-white dark:bg-[#12211F] rounded-xl border border-slate-200 dark:border-brand-border p-6" />
      </div>
    );
  }

  // Render: Not Found Error State
  if (error || !project) {
    return (
      <div className="max-w-2xl mx-auto my-12 p-8 text-center bg-white dark:bg-[#12211F] rounded-2xl border border-slate-200 dark:border-brand-border shadow-sm space-y-5">
        <div className="mx-auto w-14 h-14 rounded-full bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 flex items-center justify-center">
          <AlertCircle className="w-8 h-8" />
        </div>
        <div className="space-y-1.5">
          <h2 className="text-xl font-bold text-brand-navy dark:text-brand-ink">Project Not Found</h2>
          <p className="text-sm text-slate-500 dark:text-brand-muted max-w-md mx-auto">
            {error || `The project requested with ID "${id}" does not exist, has been deleted, or you do not have permission to access it.`}
          </p>
        </div>
        <div className="pt-2 flex justify-center space-x-3">
          <Button
            variant="outline"
            onClick={() => navigate('/projects')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Return to Projects</span>
          </Button>
          <Button
            onClick={() => fetchProjectData()}
            className="bg-brand text-white hover:bg-brand-hover"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            <span>Retry</span>
          </Button>
        </div>
      </div>
    );
  }

  // Visual Gradient for Progress Bar
  const progressGradient =
    computedProgressPercent >= 80
      ? 'from-emerald-500 to-teal-500'
      : computedProgressPercent >= 50
      ? 'from-brand-teal to-teal-400'
      : 'from-amber-500 to-teal-500';

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-in fade-in duration-200">
      {/* 1. Header Navigation & Breadcrumbs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-2 border-b border-slate-200/80 dark:border-brand-border/60">
        <div className="flex items-center space-x-2 text-xs sm:text-sm">
          <Link
            to="/projects"
            className="text-slate-500 hover:text-brand-teal transition-colors font-medium flex items-center space-x-1"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-0.5" />
            <span>Projects</span>
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
          <span className="font-semibold text-brand-navy dark:text-brand-ink truncate max-w-[200px] sm:max-w-xs">
            {project.name}
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchProjectData(true)}
            disabled={refreshing}
            className="text-xs text-slate-600 dark:text-brand-muted h-8"
            title="Refresh project data"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Sync</span>
          </Button>

          {canManage && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateAndCopyPortalLink}
              disabled={generatingPortalToken}
              className="text-xs h-8 border-teal-500/40 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-950/30 font-medium"
              title="Generate & copy cryptographically secure client guest portal share link"
            >
              {portalLinkCopied ? (
                <>
                  <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-500" />
                  <span>Link Copied!</span>
                </>
              ) : (
                <>
                  <Share2 className="w-3.5 h-3.5 mr-1.5 text-teal-600 dark:text-teal-400" />
                  <span>Generate & Copy Share Link</span>
                </>
              )}
            </Button>
          )}

          {canManage && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const token = project.portal_token;
                const url = token ? `#/portal?token=${token}` : `#/portal`;
                window.open(`${window.location.origin}${window.location.pathname}${url}`, '_blank');
              }}
              className="text-xs h-8 border-teal-500/40 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-950/30 font-medium"
              title="Open branded Client Onboarding & Account Portal in new tab"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5 text-teal-600 dark:text-teal-400" />
              <span>Open Client Portal</span>
            </Button>
          )}

          {canManage && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEditProjectModal(true)}
              className="text-xs h-8 border-slate-300 dark:border-brand-border"
            >
              <Edit3 className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
              <span>Edit Project</span>
            </Button>
          )}

          {isOwner && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteProjectModal(true)}
              className="text-xs h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              <span>Delete</span>
            </Button>
          )}
        </div>
      </div>

      {/* 2. Hero Project Title & Attributes Card */}
      <div className="bg-white dark:bg-[#12211F] rounded-2xl border border-slate-200 dark:border-brand-border p-5 sm:p-6 shadow-xs space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="space-y-2">
            {/* Badges Row */}
            <div className="flex flex-wrap items-center gap-2">
              <PlatformBadge platform={project.platform} size="sm" />
              <Badge variant={project.status as any} className="text-xs capitalize">
                {project.status.replace('_', ' ')}
              </Badge>
              <Badge variant={project.priority as any} className="text-xs uppercase flex items-center space-x-1">
                <Flag className="w-3 h-3 mr-0.5" />
                <span>{project.priority} Priority</span>
              </Badge>
              <DeliveryCountdownBadge deadline={project.deadline} status={project.status} size="sm" />
              {project.category && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  {project.category}
                </span>
              )}
            </div>

            {/* Project Title */}
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-brand-navy dark:text-brand-ink">
              {project.name}
            </h1>

            {/* Client & External Meta */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-brand-muted">
              {project.client_name && (
                <span className="flex items-center space-x-1.5">
                  <span className="text-slate-400">Client:</span>
                  <strong className="text-brand-navy dark:text-brand-ink font-semibold">
                    {project.client_name}
                  </strong>
                  {project.client_username && (
                    <span className="text-brand-teal font-mono">(@{project.client_username})</span>
                  )}
                </span>
              )}

              {project.platform_order_id && (
                <span className="flex items-center space-x-1.5">
                  <span className="text-slate-400">Order ID:</span>
                  <span className="font-mono bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[11px] font-semibold text-slate-700 dark:text-slate-300">
                    #{project.platform_order_id}
                  </span>
                </span>
              )}

              {project.platform_url && formatExternalUrl(project.platform_url) && (
                <a
                  href={formatExternalUrl(project.platform_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-1 text-brand-teal hover:underline font-semibold"
                >
                  <span>View on {project.platform || 'Platform'}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div className="flex items-center space-x-2 pt-2 lg:pt-0">
            <Button
              variant="outline"
              onClick={() => setShowDeliveryModal(true)}
              className="text-xs h-9 px-3 border-brand-teal text-brand-teal hover:bg-teal-50 dark:hover:bg-teal-950/30 font-semibold flex items-center space-x-1.5"
              title="Open Channel Delivery & Handover Message Generator"
            >
              <Send className="w-3.5 h-3.5" />
              <span>Channel Delivery</span>
            </Button>

            {!isViewer && (
              <Button
                onClick={() => setShowAddTaskModal(true)}
                className="bg-brand text-brand-inverse hover:bg-brand-hover text-xs h-9 px-3.5 shadow-xs font-semibold flex items-center space-x-1.5"
              >
                <Plus className="w-4 h-4" />
                <span>Add Task</span>
              </Button>
            )}

            {canManage && (
              <Button
                variant="outline"
                onClick={() => {
                  setEditingMilestone(null);
                  setMilestoneForm({
                    title: '',
                    description: '',
                    due_date: '',
                    status: 'pending',
                    order: milestones.length + 1,
                  });
                  setShowAddMilestoneModal(true);
                }}
                className="text-xs h-9 px-3 border-brand-teal text-brand-teal hover:bg-teal-50 dark:hover:bg-teal-950/30 font-semibold flex items-center space-x-1.5"
              >
                <Flag className="w-3.5 h-3.5" />
                <span>Add Milestone</span>
              </Button>
            )}
          </div>
        </div>

        {/* Channel Net Profit Margin & Effective Hourly Yield Banner */}
        {project.budget !== undefined && project.budget !== null && Number(project.budget) > 0 && (() => {
          const cut = calculatePlatformCut(project.budget, project.platform);
          const yieldPerHour = calculateEffectiveHourlyYield(cut.netRevenue, recordedEffortHours);
          return (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 dark:bg-emerald-950/20 p-3 sm:p-4 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-brand-navy dark:text-brand-ink text-sm">
                    ${Number(project.budget).toLocaleString()} Gross → ${cut.netRevenue.toLocaleString()} Net Agency Take-Home ({cut.marginPercent}%)
                  </div>
                  <div className="text-slate-500 text-[11px] flex items-center space-x-2">
                    <span>Platform Commission: {cut.commissionPercent}% (${cut.platformCut.toLocaleString()})</span>
                    <span>•</span>
                    <span>Effective Hourly Yield: <strong className="text-emerald-600 dark:text-emerald-400">${yieldPerHour}/hr</strong></span>
                    {project.recorded_hours ? <span>({project.recorded_hours} hrs logged)</span> : null}
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDeliveryModal(true)}
                className="h-8 text-xs font-semibold border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                <span>Generate Delivery Note</span>
              </Button>
            </div>
          );
        })()}

        {/* Project Description (if set) */}
        {project.description && (
          <p className="text-xs sm:text-sm text-slate-600 dark:text-brand-muted leading-relaxed pt-2 border-t border-slate-100 dark:border-brand-border/40">
            {project.description}
          </p>
        )}
      </div>

      {/* 3. KPI Summary Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Progress % */}
        <div className="rounded-xl border border-slate-200 dark:border-brand-border/70 bg-white dark:bg-[#12211F] p-3.5 flex flex-col justify-between shadow-2xs hover:border-brand-teal/40 transition-colors">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span className="font-medium">Progress</span>
            {project.progress_mode === 'manual' ? (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/50">
                Manual
              </span>
            ) : (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-teal-50 dark:bg-teal-950/40 text-brand-teal border border-teal-200 dark:border-teal-800/50">
                Auto
              </span>
            )}
          </div>
          <div className="my-1.5">
            <div className="text-2xl font-bold tracking-tight text-brand-navy dark:text-brand-ink tabular-nums">
              {computedProgressPercent}%
            </div>
            <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden mt-1.5">
              <div
                className={`h-full bg-gradient-to-r ${progressGradient} transition-all duration-500`}
                style={{ width: `${computedProgressPercent}%` }}
              />
            </div>
          </div>
          {project.manual_override_reason ? (
            <p className="text-[10px] text-amber-700 dark:text-amber-400 truncate" title={project.manual_override_reason}>
              Note: {project.manual_override_reason}
            </p>
          ) : (
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              {computedProgressPercent === 100 ? 'Completed' : 'Active tracking'}
            </span>
          )}
        </div>

        {/* Tasks KPI */}
        <div className="rounded-xl border border-slate-200 dark:border-brand-border/70 bg-white dark:bg-[#12211F] p-3.5 flex flex-col justify-between shadow-2xs hover:border-brand-teal/40 transition-colors">
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Tasks Completed</div>
          <div className="my-1.5 text-2xl font-bold tracking-tight text-brand-navy dark:text-brand-ink tabular-nums">
            {completedTasksCount} <span className="text-sm font-normal text-slate-400">/ {totalTasksCount}</span>
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1 truncate">
            <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
            <span>{totalTasksCount > 0 ? Math.round((completedTasksCount / totalTasksCount) * 100) : 0}% velocity</span>
          </div>
        </div>

        {/* Open Tasks KPI */}
        <div className="rounded-xl border border-slate-200 dark:border-brand-border/70 bg-white dark:bg-[#12211F] p-3.5 flex flex-col justify-between shadow-2xs hover:border-brand-teal/40 transition-colors">
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Open Tasks</div>
          <div className="my-1.5 text-2xl font-bold tracking-tight text-brand-navy dark:text-brand-ink tabular-nums">
            {openTasksCount}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {openTasksCount === 0 ? 'All tasks complete' : 'In pipeline & active'}
          </div>
        </div>

        {/* Milestones KPI */}
        <div className="rounded-xl border border-slate-200 dark:border-brand-border/70 bg-white dark:bg-[#12211F] p-3.5 flex flex-col justify-between shadow-2xs hover:border-brand-teal/40 transition-colors">
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Milestones</div>
          <div className="my-1.5 text-2xl font-bold tracking-tight text-brand-navy dark:text-brand-ink tabular-nums">
            {completedMilestonesCount} <span className="text-sm font-normal text-slate-400">/ {totalMilestonesCount}</span>
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {pendingMilestonesCount} pending deliverable{pendingMilestonesCount === 1 ? '' : 's'}
          </div>
        </div>

        {/* Recorded Hours */}
        <div className="rounded-xl border border-slate-200 dark:border-brand-border/70 bg-white dark:bg-[#12211F] p-3.5 flex flex-col justify-between shadow-2xs hover:border-brand-teal/40 transition-colors">
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Effort Logged</div>
          <div className="my-1.5 text-2xl font-bold tracking-tight text-brand-navy dark:text-brand-ink tabular-nums">
            {recordedEffortHours} <span className="text-sm font-normal text-slate-400">hrs</span>
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1 truncate">
            <Clock className="w-3 h-3 text-brand-teal flex-shrink-0" />
            <span>Tracked hours</span>
          </div>
        </div>

        {/* Target Delivery */}
        <div className="rounded-xl border border-slate-200 dark:border-brand-border/70 bg-white dark:bg-[#12211F] p-3.5 flex flex-col justify-between shadow-2xs hover:border-brand-teal/40 transition-colors">
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Target Delivery</div>
          <div className="my-1.5 text-sm sm:text-base font-bold text-brand-navy dark:text-brand-ink truncate" title={formatDateSafe(project.deadline)}>
            {formatDateSafe(project.deadline)}
          </div>
          <div className="text-[11px]">
            <DeliveryCountdownBadge deadline={project.deadline} status={project.status} size="xs" />
          </div>
        </div>
      </div>

      {/* 4. Tabbed Sections Navigation */}
      <div className="flex border-b border-slate-200 dark:border-brand-border/70 space-x-2 sm:space-x-4">
        <button
          onClick={() => setActiveTab('overview')}
          className={`py-2.5 px-3 sm:px-4 text-xs sm:text-sm font-semibold border-b-2 transition-all flex items-center space-x-2 ${
            activeTab === 'overview'
              ? 'border-brand-teal text-brand-teal'
              : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
          }`}
        >
          <Flag className="w-4 h-4" />
          <span>Overview & Milestones</span>
          <span className="ml-1 text-[10px] px-1.5 py-0.2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600">
            {milestones.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('tasks')}
          className={`py-2.5 px-3 sm:px-4 text-xs sm:text-sm font-semibold border-b-2 transition-all flex items-center space-x-2 ${
            activeTab === 'tasks'
              ? 'border-brand-teal text-brand-teal'
              : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
          }`}
        >
          <Kanban className="w-4 h-4" />
          <span>Tasks Kanban / List</span>
          <span className="ml-1 text-[10px] px-1.5 py-0.2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600">
            {tasks.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('team_activity')}
          className={`py-2.5 px-3 sm:px-4 text-xs sm:text-sm font-semibold border-b-2 transition-all flex items-center space-x-2 ${
            activeTab === 'team_activity'
              ? 'border-brand-teal text-brand-teal'
              : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
          }`}
        >
          <Activity className="w-4 h-4" />
          <span>Team & Activity</span>
          <span className="ml-1 text-[10px] px-1.5 py-0.2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600">
            {activityLogs.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('blockers')}
          className={`py-2.5 px-3 sm:px-4 text-xs sm:text-sm font-semibold border-b-2 transition-all flex items-center space-x-2 ${
            activeTab === 'blockers'
              ? 'border-brand-teal text-brand-teal'
              : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
          }`}
        >
          <AlertOctagon className="w-4 h-4 text-red-500" />
          <span>Blockers & AI Guidance</span>
          <span className="ml-1 text-[10px] px-1.5 py-0.2 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-bold">
            {projectBlockers.length}
          </span>
        </button>

        {isOwner && (
          <button
            onClick={() => setActiveTab('invoices')}
            className={`py-2.5 px-3 sm:px-4 text-xs sm:text-sm font-semibold border-b-2 transition-all flex items-center space-x-2 ${
              activeTab === 'invoices'
                ? 'border-brand-teal text-brand-teal'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
            }`}
          >
            <Receipt className="w-4 h-4 text-brand-teal" />
            <span>Invoices & Billing</span>
            <span className="ml-1 text-[10px] px-1.5 py-0.2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 font-mono">
              {projectInvoices.length}
            </span>
          </button>
        )}
      </div>

      {/* 5. Tab Content: Overview & Milestones */}
      {activeTab === 'overview' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          {/* Milestones Card */}
          <div className="bg-white dark:bg-[#12211F] rounded-2xl border border-slate-200 dark:border-brand-border p-5 sm:p-6 shadow-xs space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h3 className="text-base font-bold text-brand-navy dark:text-brand-ink flex items-center space-x-2">
                  <Flag className="w-4 h-4 text-brand-teal" />
                  <span>Roadmap Milestones & Deliverables</span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-brand-muted mt-0.5">
                  Track phased stages, deliverables, client signoffs, and milestones
                </p>
              </div>

              {canManage && (
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingMilestone(null);
                    setMilestoneForm({
                      title: '',
                      description: '',
                      due_date: '',
                      status: 'pending',
                      order: milestones.length + 1,
                    });
                    setShowAddMilestoneModal(true);
                  }}
                  className="bg-brand-teal text-white hover:bg-brand-teal/90 text-xs h-8 px-3 font-semibold flex items-center space-x-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add Milestone</span>
                </Button>
              )}
            </div>

            {/* Visual Multi-Milestone Timeline Bar */}
            {milestones.length > 0 && (() => {
              const allMilestoneTasks = tasks.filter((t) =>
                sortedMilestones.some((m) => m.id === t.milestone_id || String(m.id) === String(t.milestone_id))
              );
              const completedMilestoneTasks = allMilestoneTasks.filter((t) =>
                ['done', 'completed'].includes((t.status || '').toLowerCase())
              );
              const milestoneTaskProgress =
                allMilestoneTasks.length > 0
                  ? Math.round((completedMilestoneTasks.length / allMilestoneTasks.length) * 100)
                  : Math.round((completedMilestonesCount / milestones.length) * 100);

              return (
                <div className="p-4 bg-slate-50 dark:bg-[#162A27] rounded-xl border border-slate-200/80 dark:border-brand-border/60 space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-slate-600 dark:text-brand-muted font-medium gap-1">
                    <span className="font-semibold text-brand-navy dark:text-brand-ink">Milestone Velocity</span>
                    <span>
                      {completedMilestonesCount} of {milestones.length} Delivered ({Math.round((completedMilestonesCount / milestones.length) * 100)}%)
                      {allMilestoneTasks.length > 0 && (
                        <span className="ml-1.5 font-semibold text-brand-teal">
                          • {completedMilestoneTasks.length}/{allMilestoneTasks.length} Linked Tasks Done ({milestoneTaskProgress}%)
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 dark:bg-slate-700 h-3 rounded-full overflow-hidden flex">
                    {sortedMilestones.map((m) => {
                      const mTasks = tasks.filter((t) => t.milestone_id === m.id || String(t.milestone_id) === String(m.id));
                      const mCompleted = mTasks.filter((t) => ['done', 'completed'].includes((t.status || '').toLowerCase())).length;
                      const hasLinked = mTasks.length > 0;
                      const isDone = hasLinked ? (mCompleted === mTasks.length) : (m.status === 'completed');
                      const isInProgress = !isDone && (m.status === 'in_progress' || (hasLinked && mCompleted > 0));

                      return (
                        <div
                          key={m.id}
                          className={`h-full border-r border-white/40 dark:border-black/30 transition-all ${
                            isDone
                              ? 'bg-emerald-500'
                              : isInProgress
                              ? 'bg-amber-400 animate-pulse'
                              : 'bg-slate-300 dark:bg-slate-600'
                          }`}
                          style={{ width: `${100 / sortedMilestones.length}%` }}
                          title={`${m.title} (${isDone ? 'Completed' : isInProgress ? 'In Progress' : 'Pending'}${hasLinked ? ` — ${mCompleted}/${mTasks.length} tasks` : ''})`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                    <span>Phase 1</span>
                    <span>Phase {sortedMilestones.length}</span>
                  </div>
                </div>
              );
            })()}

            {/* Milestones Deliverable Cards List */}
            {sortedMilestones.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-slate-200 dark:border-brand-border rounded-xl space-y-3">
                <Flag className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600" />
                <div className="text-sm font-semibold text-slate-700 dark:text-brand-ink">
                  No milestones added yet
                </div>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Break this project into high-impact delivery milestones to track roadmap velocity and handoffs.
                </p>
                {canManage && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setMilestoneForm({
                        title: '',
                        description: '',
                        due_date: '',
                        status: 'pending',
                        order: 1,
                      });
                      setShowAddMilestoneModal(true);
                    }}
                    className="bg-brand-teal text-white text-xs h-8 px-3"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    <span>Create First Milestone</span>
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {sortedMilestones.map((m, idx) => {
                  const linkedTasks = tasks.filter((t) => t.milestone_id === m.id || String(t.milestone_id) === String(m.id));
                  const completedLinked = linkedTasks.filter((t) => ['done', 'completed'].includes((t.status || '').toLowerCase())).length;

                  return (
                    <div
                      key={m.id}
                      className="p-4 rounded-xl border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] hover:border-brand-teal/40 transition-colors space-y-3"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-mono font-bold text-slate-400">
                              #{idx + 1}
                            </span>
                            <h4 className="font-bold text-sm text-brand-navy dark:text-brand-ink">
                              {m.title}
                            </h4>
                          </div>
                          {m.description && (
                            <p className="text-xs text-slate-600 dark:text-brand-muted leading-relaxed whitespace-pre-line">
                              {m.description}
                            </p>
                          )}
                        </div>

                        {/* Status Toggle & Actions */}
                        <div className="flex items-center space-x-2 self-start sm:self-auto shrink-0 flex-wrap gap-y-1">
                          {!isViewer && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setNewTaskForm({
                                  title: '',
                                  description: '',
                                  priority: 'medium',
                                  assignee_id: '',
                                  milestone_id: m.id,
                                  due_date: m.due_date ? m.due_date.substring(0, 10) : '',
                                  estimated_hours: 2,
                                });
                                setShowAddTaskModal(true);
                              }}
                              className="h-7 text-xs px-2 text-brand-teal border-brand-teal/40 hover:bg-brand-teal/10 flex items-center space-x-1 font-semibold"
                              title={`Add task to ${m.title}`}
                            >
                              <Plus className="w-3 h-3 mr-0.5" />
                              <span>+ Add task</span>
                            </Button>
                          )}

                          <button
                            type="button"
                            onClick={() => handleToggleMilestoneStatus(m)}
                            disabled={!canManage}
                            className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
                              !canManage ? 'cursor-default opacity-85' : 'cursor-pointer'
                            } ${
                              m.status === 'completed'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300'
                                : m.status === 'in_progress'
                                ? 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300'
                                : 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-300'
                            }`}
                            title={canManage ? "Click to cycle status: Pending → In Progress → Completed" : "Milestone status"}
                          >
                            {m.status === 'completed' ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            ) : m.status === 'in_progress' ? (
                              <Clock className="w-3.5 h-3.5 text-amber-600 animate-spin" />
                            ) : (
                              <Circle className="w-3.5 h-3.5 text-slate-400" />
                            )}
                            <span className="capitalize">{m.status.replace('_', ' ')}</span>
                          </button>

                          {canManage && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingMilestone(m);
                                  setMilestoneForm({
                                    title: m.title,
                                    description: m.description || '',
                                    due_date: m.due_date ? m.due_date.substring(0, 10) : '',
                                    status: m.status,
                                    order: m.order || idx + 1,
                                  });
                                }}
                                className="h-7 w-7 p-0 text-slate-400 hover:text-brand-teal"
                                title="Edit milestone"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteMilestoneTarget(m)}
                                className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
                                title="Delete milestone"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Deliverable Meta (Due date & Linked tasks) */}
                      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 pt-2 border-t border-slate-100 dark:border-brand-border/40">
                        <div className="flex items-center space-x-4">
                          {m.due_date && (
                            <span className="flex items-center space-x-1">
                              <Calendar className="w-3.5 h-3.5 text-slate-400" />
                              <span>
                                Due {formatDateSafe(m.due_date)}
                              </span>
                            </span>
                          )}

                          <span className="flex items-center space-x-1 font-medium">
                            <CheckCircle2 className="w-3.5 h-3.5 text-slate-400" />
                            <span>
                              {completedLinked} / {linkedTasks.length} linked tasks completed
                            </span>
                          </span>
                        </div>
                        {linkedTasks.length > 0 && (
                          <span className="font-mono text-[11px] font-semibold text-brand-teal">
                            {Math.round((completedLinked / linkedTasks.length) * 100)}%
                          </span>
                        )}
                      </div>

                      {/* Mini Progress Bar if tasks are linked */}
                      {linkedTasks.length > 0 && (
                        <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                          <div
                            className="bg-brand-teal h-full transition-all duration-300"
                            style={{ width: `${Math.round((completedLinked / linkedTasks.length) * 100)}%` }}
                          />
                        </div>
                      )}

                      {/* Linked Tasks List */}
                      <div className="pt-2 space-y-2 border-t border-slate-100 dark:border-brand-border/30">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                          <span>Linked Tasks ({linkedTasks.length})</span>
                          {!isViewer && (
                            <button
                              type="button"
                              onClick={() => {
                                setNewTaskForm({
                                  title: '',
                                  description: '',
                                  priority: 'medium',
                                  assignee_id: '',
                                  milestone_id: m.id,
                                  due_date: m.due_date ? m.due_date.substring(0, 10) : '',
                                  estimated_hours: 2,
                                });
                                setShowAddTaskModal(true);
                              }}
                              className="text-brand-teal hover:underline text-[11px] font-semibold flex items-center space-x-0.5 lowercase capitalize"
                            >
                              <Plus className="w-3 h-3 inline" />
                              <span>+ Add task</span>
                            </button>
                          )}
                        </div>
                        {linkedTasks.length === 0 ? (
                          <div className="text-xs text-slate-400 italic py-1.5 px-2.5 rounded bg-slate-50/50 dark:bg-[#162A27]/30 border border-dashed border-slate-200 dark:border-brand-border/40 flex items-center justify-between">
                            <span>No tasks linked to this milestone yet.</span>
                            {!isViewer && (
                              <button
                                type="button"
                                onClick={() => {
                                  setNewTaskForm({
                                    title: '',
                                    description: '',
                                    priority: 'medium',
                                    assignee_id: '',
                                    milestone_id: m.id,
                                    due_date: m.due_date ? m.due_date.substring(0, 10) : '',
                                    estimated_hours: 2,
                                  });
                                  setShowAddTaskModal(true);
                                }}
                                className="text-brand-teal hover:underline font-semibold ml-2 not-italic text-xs"
                              >
                                + Add task
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            {linkedTasks.map((t) => (
                              <div
                                key={t.id}
                                className="p-2 rounded-lg bg-slate-50/60 dark:bg-[#162A27]/50 border border-slate-100 dark:border-brand-border/40 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs"
                              >
                                <div className="flex items-center space-x-2 min-w-0 flex-1">
                                  <span
                                    className={`w-2 h-2 rounded-full shrink-0 ${
                                      ['done', 'completed'].includes((t.status || '').toLowerCase())
                                        ? 'bg-emerald-500'
                                        : t.status === 'in_progress'
                                        ? 'bg-blue-500'
                                        : t.status === 'blocked'
                                        ? 'bg-rose-500'
                                        : 'bg-slate-300'
                                    }`}
                                  />
                                  <Link
                                    to={`/tasks/${t.id}`}
                                    className="font-medium text-slate-800 dark:text-brand-ink hover:text-brand-teal truncate"
                                    title={t.title}
                                  >
                                    {t.title}
                                  </Link>
                                  <Badge variant={t.status as any} className="text-[10px] px-1.5 py-0 capitalize shrink-0">
                                    {t.status.replace('_', ' ')}
                                  </Badge>
                                  <Badge variant={t.priority as any} className="text-[9px] uppercase px-1 py-0 shrink-0">
                                    {t.priority}
                                  </Badge>
                                  {t.assignee_name && (
                                    <span className="text-[11px] text-slate-400 truncate shrink-0">
                                      • {t.assignee_name}
                                    </span>
                                  )}
                                </div>

                                {/* Controls to Move to another milestone or Unlink */}
                                {!isViewer && canEditTask(t) && (
                                  <div className="flex items-center space-x-1.5 self-end sm:self-auto shrink-0">
                                    <select
                                      value={t.milestone_id || ''}
                                      onChange={(e) => handleMoveTaskMilestone(t.id, e.target.value || null)}
                                      className="h-6 text-[11px] rounded border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] text-slate-700 dark:text-brand-ink px-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
                                      title="Move to another milestone of this project"
                                    >
                                      <option value={m.id}>{m.title} (current)</option>
                                      {sortedMilestones
                                        .filter((other) => other.id !== m.id)
                                        .map((other) => (
                                          <option key={other.id} value={other.id}>
                                            Move to: {other.title}
                                          </option>
                                        ))}
                                      <option value="">Unlink from milestone</option>
                                    </select>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleMoveTaskMilestone(t.id, null)}
                                      className="h-6 px-1.5 text-[10px] text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                                      title="Unlink task from milestone"
                                    >
                                      Unlink
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Project Metadata Card */}
          <div className="bg-white dark:bg-[#12211F] rounded-2xl border border-slate-200 dark:border-brand-border p-5 sm:p-6 shadow-xs space-y-4">
            <h3 className="text-base font-bold text-brand-navy dark:text-brand-ink flex items-center space-x-2">
              <Briefcase className="w-4 h-4 text-brand-teal" />
              <span>Project Specification & Parameters</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg space-y-1">
                <span className="text-slate-400 block font-medium">Category</span>
                <span className="font-semibold text-brand-navy dark:text-brand-ink">{project.category || 'Standard'}</span>
              </div>

              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg space-y-1">
                <span className="text-slate-400 block font-medium">Platform Channel</span>
                <span className="font-semibold text-brand-navy dark:text-brand-ink capitalize">{project.platform || 'Direct'}</span>
              </div>

              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg space-y-1">
                <span className="text-slate-400 block font-medium">Progress Mode</span>
                <span className="font-semibold text-brand-navy dark:text-brand-ink capitalize">
                  {project.progress_mode || 'Automatic'}
                </span>
              </div>

              {isOwner && (project as any).budget !== undefined && (
                <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg space-y-1">
                  <span className="text-slate-400 block font-medium">Budget & Currency</span>
                  <span className="font-semibold text-brand-navy dark:text-brand-ink">
                    {(project as any).budget ? `${(project as any).currency || 'PKR'} ${Number((project as any).budget).toLocaleString()}` : 'Unspecified'}
                  </span>
                </div>
              )}

              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg space-y-1">
                <span className="text-slate-400 block font-medium">Created At</span>
                <span className="font-semibold text-brand-navy dark:text-brand-ink">
                  {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>

              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg space-y-1">
                <span className="text-slate-400 block font-medium">Last Updated</span>
                <span className="font-semibold text-brand-navy dark:text-brand-ink">
                  {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6. Tab Content: Tasks Kanban / List */}
      {activeTab === 'tasks' && (
        <div className="space-y-4 animate-in fade-in duration-150">
          {/* Controls Bar: Search, Status Filter, View Toggle, Add Task */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-[#12211F] p-4 rounded-xl border border-slate-200 dark:border-brand-border">
            <div className="flex flex-wrap items-center gap-2 flex-1">
              <div className="relative min-w-[200px] flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
                <Input
                  placeholder="Search project tasks..."
                  value={taskSearch}
                  onChange={(e) => setTaskSearch(e.target.value)}
                  className="pl-8 text-xs h-8"
                />
              </div>

              <select
                value={taskStatusFilter}
                onChange={(e) => setTaskStatusFilter(e.target.value)}
                className="text-xs h-8 px-2.5 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-slate-700 dark:text-brand-ink font-medium"
              >
                <option value="all">All Statuses ({tasks.length})</option>
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="in_review">In Review</option>
                <option value="blocked">Blocked</option>
                <option value="done">Done</option>
              </select>

              <select
                value={taskMilestoneFilter}
                onChange={(e) => setTaskMilestoneFilter(e.target.value)}
                className="text-xs h-8 px-2.5 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-slate-700 dark:text-brand-ink font-medium"
              >
                <option value="all">All Milestones</option>
                <option value="unlinked">No milestone (Unlinked)</option>
                {sortedMilestones.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2 self-end sm:self-auto">
              {/* View Switcher: Kanban vs List */}
              <div className="inline-flex rounded-lg border border-slate-200 dark:border-brand-border p-0.5 bg-slate-50 dark:bg-[#162A27]">
                <button
                  type="button"
                  onClick={() => setTaskViewMode('kanban')}
                  className={`p-1.5 rounded-md text-xs font-medium transition-colors ${
                    taskViewMode === 'kanban'
                      ? 'bg-white dark:bg-[#12211F] text-brand-navy dark:text-brand-ink shadow-xs'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                  title="Kanban Board View"
                >
                  <Kanban className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setTaskViewMode('list')}
                  className={`p-1.5 rounded-md text-xs font-medium transition-colors ${
                    taskViewMode === 'list'
                      ? 'bg-white dark:bg-[#12211F] text-brand-navy dark:text-brand-ink shadow-xs'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                  title="Table List View"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              {!isViewer && (
                <Button
                  size="sm"
                  onClick={() => setShowAddTaskModal(true)}
                  className="bg-brand text-white hover:bg-brand-hover text-xs h-8 font-semibold flex items-center space-x-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>New Task</span>
                </Button>
              )}
            </div>
          </div>

          {/* Kanban Board View */}
          {taskViewMode === 'kanban' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-start">
              {kanbanColumns.map((col) => {
                const colTasks = filteredTasks.filter((t) => t.status === col.status);

                return (
                  <div
                    key={col.status}
                    className="bg-slate-50/70 dark:bg-[#162A27]/60 rounded-xl border border-slate-200/80 dark:border-brand-border/60 p-3 flex flex-col min-h-[300px] space-y-2.5"
                  >
                    {/* Column Header */}
                    <div className="flex items-center justify-between pb-1.5 border-b border-slate-200 dark:border-brand-border/40">
                      <div className="flex items-center space-x-1.5">
                        <span className="text-xs font-bold text-brand-navy dark:text-brand-ink">
                          {col.label}
                        </span>
                        <Badge variant={col.badgeVariant} className="text-[10px] px-1.5 py-0">
                          {colTasks.length}
                        </Badge>
                      </div>
                    </div>

                    {/* Column Cards */}
                    <div className="space-y-2 flex-1">
                      {colTasks.length === 0 ? (
                        <div className="text-[11px] text-slate-400 text-center py-8 border border-dashed border-slate-200 dark:border-brand-border rounded-lg">
                          No tasks in {col.label}
                        </div>
                      ) : (
                        colTasks.map((t) => (
                          <div
                            key={t.id}
                            className="p-3 bg-white dark:bg-[#12211F] rounded-lg border border-slate-200 dark:border-brand-border shadow-2xs hover:shadow-xs hover:border-brand-teal/40 transition-all space-y-2 group"
                          >
                            <div className="flex items-start justify-between gap-1.5">
                              <Link
                                to={`/tasks/${t.id}`}
                                className="font-semibold text-xs text-brand-navy dark:text-brand-ink group-hover:text-brand-teal transition-colors line-clamp-2 leading-snug"
                              >
                                {t.title}
                              </Link>
                              <Badge variant={t.priority as any} className="text-[9px] uppercase px-1 py-0 shrink-0">
                                {t.priority}
                              </Badge>
                            </div>

                            {t.milestone_id && (() => {
                              const m = milestones.find((m) => m.id === t.milestone_id || String(m.id) === String(t.milestone_id));
                              return m ? (
                                <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-brand-teal/10 text-brand-teal border border-brand-teal/20 font-medium truncate max-w-full" title={`Milestone: ${m.title}`}>
                                  🚩 {m.title}
                                </span>
                              ) : null;
                            })()}

                            {t.description && (
                              <p className="text-[11px] text-slate-500 line-clamp-2 leading-tight">
                                {t.description}
                              </p>
                            )}

                            {/* Task Meta Footer */}
                            <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1 border-t border-slate-100 dark:border-brand-border/30">
                              <span className="truncate max-w-[90px]" title={t.assignee_name || 'Unassigned'}>
                                {t.assignee_name || 'Unassigned'}
                              </span>

                              {t.due_date && (
                                <span className="flex items-center space-x-0.5 text-slate-400">
                                  <Clock className="w-2.5 h-2.5" />
                                  <span>{t.due_date.substring(5, 10)}</span>
                                </span>
                              )}
                            </div>

                            {/* Quick Status Shift Controls */}
                            {!isViewer && canEditTask(t) && (
                              <div className="pt-1 flex items-center justify-between gap-1">
                                <select
                                  value={t.status}
                                  onChange={(e) => handleUpdateTaskStatus(t.id, e.target.value as TaskStatus)}
                                  className="w-full text-[10px] py-0.5 px-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-brand-border rounded text-slate-600 dark:text-slate-300 font-medium"
                                >
                                  <option value="todo">Move to To Do</option>
                                  <option value="in_progress">Move to In Progress</option>
                                  <option value="in_review">Move to In Review</option>
                                  <option value="blocked">Move to Blocked</option>
                                  {canApproveDone ? (
                                    <option value="done">Move to Done</option>
                                  ) : t.status === 'done' ? (
                                    <option value="done" disabled>Done (Approved)</option>
                                  ) : (
                                    <option value="done" disabled>Done (Approval Required)</option>
                                  )}
                                </select>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Table List View */
            <div className="bg-white dark:bg-[#12211F] rounded-xl border border-slate-200 dark:border-brand-border overflow-hidden shadow-2xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-[#162A27] text-slate-500 border-b border-slate-200 dark:border-brand-border font-semibold">
                      <th className="py-2.5 px-4">Task</th>
                      <th className="py-2.5 px-3">Milestone</th>
                      <th className="py-2.5 px-3">Status</th>
                      <th className="py-2.5 px-3">Priority</th>
                      <th className="py-2.5 px-3">Assignee</th>
                      <th className="py-2.5 px-3">Due Date</th>
                      <th className="py-2.5 px-3 text-right">Hours</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-brand-border/40">
                    {filteredTasks.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-slate-400">
                          No tasks match the filter criteria.
                        </td>
                      </tr>
                    ) : (
                      filteredTasks.map((t) => (
                        <tr
                          key={t.id}
                          className="hover:bg-slate-50 dark:hover:bg-[#162A27]/50 transition-colors cursor-pointer group"
                          onClick={() => navigate(`/tasks/${t.id}`)}
                        >
                          <td className="py-2.5 px-4 font-semibold text-brand-navy dark:text-brand-ink group-hover:text-brand-teal">
                            {t.title}
                          </td>
                          <td className="py-2.5 px-3">
                            {(() => {
                              const tm = t.milestone_id ? milestones.find((m) => m.id === t.milestone_id || String(m.id) === String(t.milestone_id)) : null;
                              return tm ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-brand-teal/10 text-brand-teal border border-brand-teal/20" title={tm.title}>
                                  <Flag className="w-2.5 h-2.5 shrink-0" />
                                  <span className="truncate max-w-[130px]">{tm.title}</span>
                                </span>
                              ) : (
                                <span className="text-[11px] text-slate-400 dark:text-slate-500 italic">No milestone</span>
                              );
                            })()}
                          </td>
                          <td className="py-2.5 px-3" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={t.status}
                              disabled={isViewer || !canEditTask(t)}
                              onChange={(e) => handleUpdateTaskStatus(t.id, e.target.value as TaskStatus)}
                              className="text-[11px] font-medium rounded border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] px-1.5 py-0.5 disabled:opacity-60"
                            >
                              <option value="todo">To Do</option>
                              <option value="in_progress">In Progress</option>
                              <option value="in_review">In Review</option>
                              <option value="blocked">Blocked</option>
                              {canApproveDone ? (
                                <option value="done">Done</option>
                              ) : t.status === 'done' ? (
                                <option value="done" disabled>Done (Approved)</option>
                              ) : (
                                <option value="done" disabled>Done (Approval Required)</option>
                              )}
                            </select>
                          </td>
                          <td className="py-2.5 px-3">
                            <Badge variant={t.priority as any} className="text-[10px] uppercase">
                              {t.priority}
                            </Badge>
                          </td>
                          <td className="py-2.5 px-3 text-slate-600 dark:text-brand-muted">
                            {t.assignee_name || 'Unassigned'}
                          </td>
                          <td className="py-2.5 px-3 text-slate-500">
                            {t.due_date ? t.due_date.substring(0, 10) : '—'}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-slate-700 dark:text-slate-300">
                            {t.recorded_hours || 0}h
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 7. Tab Content: Team & Activity */}
      {activeTab === 'team_activity' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in duration-150">
          {/* Assigned Team Members Column */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white dark:bg-[#12211F] rounded-2xl border border-slate-200 dark:border-brand-border p-5 shadow-xs space-y-4">
              <div>
                <h3 className="text-base font-bold text-brand-navy dark:text-brand-ink flex items-center space-x-2">
                  <Users className="w-4 h-4 text-brand-teal" />
                  <span>Project Team Roster</span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-brand-muted mt-0.5">
                  Members actively assigned to tasks or managing this project
                </p>
              </div>

              {assignedTeam.length === 0 ? (
                <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 dark:border-brand-border rounded-lg">
                  No team members directly assigned to tasks yet.
                </div>
              ) : (
                <div className="space-y-2.5">
                  {assignedTeam.map((m) => {
                    const memberTasksCount = tasks.filter((t) => t.assignee_id === m.id || (t.assignee_name && t.assignee_name.toLowerCase() === m.name.toLowerCase())).length;

                    return (
                      <div
                        key={m.id}
                        className="p-3 rounded-xl border border-slate-200 dark:border-brand-border bg-slate-50/60 dark:bg-[#162A27]/40 flex items-center justify-between"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-brand-teal/15 text-brand-teal flex items-center justify-center font-bold text-xs uppercase">
                            {m.name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-bold text-xs text-brand-navy dark:text-brand-ink">
                              {m.name}
                            </div>
                            <div className="text-[10px] text-slate-500 capitalize">{m.role.replace('_', ' ')}</div>
                          </div>
                        </div>

                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-semibold text-slate-600">
                          {memberTasksCount} task{memberTasksCount === 1 ? '' : 's'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Real-time Project Activity Stream Column */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white dark:bg-[#12211F] rounded-2xl border border-slate-200 dark:border-brand-border p-5 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-brand-navy dark:text-brand-ink flex items-center space-x-2">
                    <Activity className="w-4 h-4 text-brand-teal" />
                    <span>Project Audit Trail & Activity Log</span>
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-brand-muted mt-0.5">
                    Immutable history of task transitions, milestone updates, and deliverables
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fetchProjectData(true)}
                  disabled={refreshing}
                  className="text-xs h-7 text-slate-500"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                  <span>Sync</span>
                </Button>
              </div>

              {activityLogs.length === 0 ? (
                <div className="text-xs text-slate-400 py-10 text-center border border-dashed border-slate-200 dark:border-brand-border rounded-lg space-y-2">
                  <Activity className="w-6 h-6 mx-auto text-slate-300" />
                  <div>No activity recorded specifically for this project yet.</div>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1 divide-y divide-slate-100 dark:divide-brand-border/30">
                  {activityLogs.map((log) => (
                    <div key={log.id} className="pt-3 first:pt-0 flex items-start space-x-3 text-xs">
                      <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">
                        {(log.actor_name || 'U').charAt(0)}
                      </div>
                      <div className="flex-1 space-y-0.5">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-brand-navy dark:text-brand-ink">
                            {log.actor_name || 'System User'}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {log.created_at ? new Date(log.created_at).toLocaleString() : ''}
                          </span>
                        </div>
                        <p className="text-slate-600 dark:text-brand-muted leading-tight">
                          {log.description}
                        </p>
                        {log.action && (
                          <span className="inline-block text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded">
                            {log.action.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 8. Tab Content: Project Blockers & AI Guidance */}
      {activeTab === 'blockers' && (
        <div className="space-y-4 animate-in fade-in duration-150">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-white dark:bg-[#12211F] border border-slate-200 dark:border-brand-border">
            <div>
              <h3 className="text-base font-bold text-brand-navy dark:text-brand-ink flex items-center space-x-2">
                <AlertOctagon className="w-4 h-4 text-red-500" />
                <span>Project Blockers & AI Diagnosis</span>
              </h3>
              <p className="text-xs text-slate-500 dark:text-brand-muted mt-0.5">
                Active impediments, AI diagnosis advice, and developer resolutions on this project
              </p>
            </div>

            <Link to="/blockers">
              <Button size="sm" className="bg-brand text-white hover:bg-brand-dark text-xs">
                <Plus className="w-3.5 h-3.5 mr-1" />
                <span>Manage / Log Blockers</span>
              </Button>
            </Link>
          </div>

          {projectBlockers.length === 0 ? (
            <div className="bg-white dark:bg-[#12211F] rounded-2xl border border-dashed border-slate-200 dark:border-brand-border p-10 text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <h4 className="text-sm font-bold text-brand-navy dark:text-brand-ink">No Blockers on this Project</h4>
              <p className="text-xs text-slate-500 dark:text-brand-muted mt-1 max-w-sm mx-auto">
                No active roadblocks are currently hindering work on this project.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {projectBlockers.map((b: any) => (
                <div
                  key={b.id}
                  className={`p-4 rounded-xl border space-y-3 ${
                    b.status === 'resolved'
                      ? 'bg-emerald-50/40 border-emerald-200'
                      : b.status === 'investigating'
                      ? 'bg-blue-50/40 border-blue-200'
                      : 'bg-red-50/40 border-red-200'
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/60 pb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                        b.status === 'resolved'
                          ? 'bg-emerald-200 text-emerald-900'
                          : b.status === 'investigating'
                          ? 'bg-blue-200 text-blue-900'
                          : 'bg-red-200 text-red-900'
                      }`}>
                        {b.status}
                      </span>
                      <span className="text-[10px] font-semibold text-slate-500 uppercase">
                        {b.severity} priority
                      </span>
                      {b.task_title && (
                        <span className="text-xs font-semibold text-slate-700 bg-white/80 px-2 py-0.5 rounded border border-slate-200">
                          Task: {b.task_title}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">
                      Logged by <strong>{b.user_name}</strong>
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    <div className="p-3 rounded-lg bg-red-100/50 border border-red-200">
                      <span className="font-bold text-red-900 block mb-1">1. What Was Blocker:</span>
                      <p className="text-slate-800">{b.blocker_description}</p>
                    </div>

                    <div className="p-3 rounded-lg bg-purple-100/50 border border-purple-200">
                      <span className="font-bold text-purple-900 block mb-1 flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-purple-600" />
                        2. What AI Said:
                      </span>
                      <p className="text-slate-800">{b.ai_diagnosis || 'No AI advice recorded.'}</p>
                    </div>

                    <div className="p-3 rounded-lg bg-emerald-100/50 border border-emerald-200">
                      <span className="font-bold text-emerald-900 block mb-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                        3. What After That:
                      </span>
                      <p className="text-slate-800">{b.resolution_notes || 'Pending resolution steps.'}</p>
                    </div>
                  </div>

                  {b.status !== 'resolved' && !isViewer && (
                    <div className="flex justify-end pt-1 border-t border-slate-200/60">
                      <Button
                        size="sm"
                        onClick={() => handleResolveProjectBlocker(b)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 px-2.5 font-semibold"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                        Mark Resolved
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 6. Tab Content: Invoices & Billing (Owner Only) */}
      {activeTab === 'invoices' && isOwner && (
        <div className="space-y-6 animate-in fade-in duration-150">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-white dark:bg-[#12211F] border border-slate-200 dark:border-brand-border">
            <div>
              <h3 className="text-base font-bold text-brand-navy dark:text-brand-ink flex items-center space-x-2">
                <Receipt className="w-4 h-4 text-brand-teal" />
                <span>Project Invoices & Receivables</span>
              </h3>
              <p className="text-xs text-slate-500 dark:text-brand-muted mt-0.5">
                Milestone deliverables invoicing and payments collected for {project.name}
              </p>
            </div>

            <Button
              size="sm"
              onClick={() => {
                navigate(`/finance?tab=invoices&action=create_invoice&project_id=${project.id}&client_id=${project.client_id || ''}`);
              }}
              className="bg-brand-teal text-white hover:bg-brand-teal-action text-xs h-8 px-3 font-semibold flex items-center space-x-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Invoice Project</span>
            </Button>
          </div>

          {/* Financial Metrics Cards Strip */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] shadow-2xs">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Project Budget</span>
              <div className="text-xl font-bold font-mono text-slate-800 dark:text-white mt-1">
                {(project as any).currency || 'USD'} {Number((project as any).budget || 0).toLocaleString()}
              </div>
            </div>

            <div className="p-4 rounded-xl border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] shadow-2xs">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Invoiced</span>
              <div className="text-xl font-bold font-mono text-brand-navy dark:text-brand-ink mt-1">
                {(project as any).currency || 'USD'} {projectInvoices.reduce((a, b) => a + (Number(b.total_amount) || 0), 0).toLocaleString()}
              </div>
            </div>

            <div className="p-4 rounded-xl border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] shadow-2xs">
              <span className="text-[11px] font-bold text-status-success uppercase tracking-wider">Collected</span>
              <div className="text-xl font-bold font-mono text-status-success mt-1">
                {(project as any).currency || 'USD'} {projectInvoices.reduce((a, b) => a + (Number(b.amount_paid) || 0), 0).toLocaleString()}
              </div>
            </div>

            <div className="p-4 rounded-xl border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] shadow-2xs">
              <span className="text-[11px] font-bold text-amber-600 uppercase tracking-wider">Remaining Balance</span>
              <div className="text-xl font-bold font-mono text-amber-600 mt-1">
                {(project as any).currency || 'USD'} {projectInvoices.reduce((a, b) => a + (Number(b.balance_due) || 0), 0).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Invoices List */}
          {projectInvoices.length === 0 ? (
            <div className="bg-white dark:bg-[#12211F] rounded-2xl border border-dashed border-slate-200 dark:border-brand-border p-10 text-center space-y-3">
              <Receipt className="w-8 h-8 text-slate-300 mx-auto" />
              <h4 className="text-sm font-bold text-brand-navy dark:text-brand-ink">No Invoices Linked to this Project</h4>
              <p className="text-xs text-slate-500 dark:text-brand-muted max-w-sm mx-auto">
                Issue milestone deliverables invoices directly linked to {project.name}.
              </p>
              <Button
                size="sm"
                onClick={() => {
                  navigate(`/finance?tab=invoices&action=create_invoice&project_id=${project.id}&client_id=${project.client_id || ''}`);
                }}
                className="bg-brand-teal text-white hover:bg-brand-teal-action text-xs"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                <span>Create First Invoice</span>
              </Button>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50 dark:bg-brand-surface border-b border-slate-200 dark:border-brand-border text-slate-500 uppercase text-[10px] font-semibold tracking-wider">
                  <tr>
                    <th className="py-3 px-4">Invoice #</th>
                    <th className="py-3 px-4">Issue Date</th>
                    <th className="py-3 px-4">Due Date</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Total Amount</th>
                    <th className="py-3 px-4 text-right">Balance Due</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-brand-border">
                  {projectInvoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-slate-50/60 dark:hover:bg-brand-surface/40 transition-colors">
                      <td className="py-3 px-4 font-mono font-bold text-brand-teal">
                        {inv.invoice_number}
                      </td>
                      <td className="py-3 px-4 font-mono text-slate-600 dark:text-slate-300">
                        {inv.issue_date}
                      </td>
                      <td className="py-3 px-4 font-mono text-slate-600 dark:text-slate-300">
                        {inv.due_date || 'On Receipt'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                          inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' :
                          inv.status === 'overdue' ? 'bg-red-50 text-red-700' :
                          inv.status === 'partial' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
                        }`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono font-semibold text-right text-slate-800 dark:text-slate-200">
                        {inv.currency} {Number(inv.total_amount).toLocaleString()}
                      </td>
                      <td className={`py-3 px-4 font-mono font-bold text-right ${
                        inv.balance_due > 0 ? 'text-amber-600' : 'text-emerald-600'
                      }`}>
                        {inv.currency} {Number(inv.balance_due).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/finance?tab=invoices&search=${inv.invoice_number}`)}
                          className="text-xs text-brand-teal hover:underline h-7 px-2"
                        >
                          Manage in Finance
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* MODAL: Edit Project */}
      {showEditProjectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white dark:bg-[#12211F] rounded-2xl max-w-xl w-full p-6 shadow-xl border border-slate-200 dark:border-brand-border space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-brand-border/40">
              <h3 className="font-bold text-base text-brand-navy dark:text-brand-ink flex items-center space-x-2">
                <Edit3 className="w-4 h-4 text-brand-teal" />
                <span>Edit Project Settings</span>
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditProjectModal(false)}
                className="h-7 w-7 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <form onSubmit={handleSaveProjectEdit} className="space-y-4 text-xs">
              <div className="space-y-1">
                <label className="font-semibold text-slate-700 dark:text-brand-ink">Project Name *</label>
                <Input
                  required
                  value={editProjectForm.name}
                  onChange={(e) => setEditProjectForm({ ...editProjectForm, name: e.target.value })}
                  placeholder="e.g. Next-Gen WooCommerce Portal"
                  className="h-8 text-xs"
                />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-slate-700 dark:text-brand-ink">Description</label>
                <textarea
                  value={editProjectForm.description}
                  onChange={(e) => setEditProjectForm({ ...editProjectForm, description: e.target.value })}
                  rows={3}
                  className="w-full text-xs p-2 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-brand-navy dark:text-brand-ink"
                  placeholder="Project scope and details..."
                />
                <TOSComplianceGuard
                  text={editProjectForm.description || ''}
                  platform={editProjectForm.platform || project?.platform}
                  onCleanText={(cleaned) => setEditProjectForm({ ...editProjectForm, description: cleaned })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Category</label>
                  <Input
                    value={editProjectForm.category}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, category: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Status</label>
                  <select
                    value={editProjectForm.status}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, status: e.target.value })}
                    className="w-full h-8 px-2 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-xs text-brand-navy dark:text-brand-ink"
                  >
                    <option value="active">Active</option>
                    <option value="on_hold">On Hold</option>
                    <option value="at_risk">At Risk</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Priority</label>
                  <select
                    value={editProjectForm.priority}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, priority: e.target.value })}
                    className="w-full h-8 px-2 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-xs text-brand-navy dark:text-brand-ink"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Deadline</label>
                  <Input
                    type="date"
                    value={editProjectForm.deadline}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, deadline: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Platform Channel</label>
                  <select
                    value={editProjectForm.platform}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, platform: e.target.value as PlatformType })}
                    className="w-full h-8 px-2 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-xs text-brand-navy dark:text-brand-ink"
                  >
                    <option value="direct">Direct</option>
                    <option value="fiverr">Fiverr</option>
                    <option value="upwork">Upwork</option>
                    <option value="internal">Internal</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Client Name</label>
                  <Input
                    value={editProjectForm.client_name}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, client_name: e.target.value })}
                    placeholder="e.g. Acme Corp"
                    className="h-8 text-xs"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Client Username</label>
                  <Input
                    value={editProjectForm.client_username}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, client_username: e.target.value })}
                    placeholder="e.g. acme_buyer"
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Platform URL</label>
                  <Input
                    value={editProjectForm.platform_url}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, platform_url: e.target.value })}
                    placeholder="https://fiverr.com/orders/..."
                    className="h-8 text-xs"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Platform Order ID</label>
                  <Input
                    value={editProjectForm.platform_order_id}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, platform_order_id: e.target.value })}
                    placeholder="e.g. FO94812"
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              {/* Progress Mode & Manual Override */}
              <div className="p-3 bg-slate-50 dark:bg-[#162A27] rounded-lg border border-slate-200 dark:border-brand-border space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800 dark:text-brand-ink block">Progress Mode</span>
                    <span className="text-[10px] text-slate-500">Calculate dynamically from tasks or set explicit manual value</span>
                  </div>
                  <select
                    value={editProjectForm.progress_mode}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, progress_mode: e.target.value })}
                    className="h-7 text-xs px-2 rounded border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F]"
                  >
                    <option value="automatic">Automatic (Task Mean)</option>
                    <option value="manual">Manual Override</option>
                  </select>
                </div>

                {editProjectForm.progress_mode === 'manual' && (
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200 dark:border-brand-border/40 animate-in fade-in">
                    <div className="space-y-1">
                      <label className="font-semibold text-slate-700 dark:text-brand-ink">Override Progress %</label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={editProjectForm.progress_percent}
                        onChange={(e) => setEditProjectForm({ ...editProjectForm, progress_percent: Number(e.target.value) })}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="font-semibold text-slate-700 dark:text-brand-ink">Override Reason</label>
                      <Input
                        value={editProjectForm.manual_override_reason}
                        onChange={(e) => setEditProjectForm({ ...editProjectForm, manual_override_reason: e.target.value })}
                        placeholder="e.g. Awaiting client review"
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Owner Financial Budget */}
              {isOwner && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 dark:bg-[#162A27] rounded-lg border border-slate-200 dark:border-brand-border">
                  <div className="space-y-1">
                    <label className="font-semibold text-slate-700 dark:text-brand-ink">Budget Amount</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editProjectForm.budget}
                      onChange={(e) => setEditProjectForm({ ...editProjectForm, budget: e.target.value })}
                      placeholder="e.g. 50000"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="font-semibold text-slate-700 dark:text-brand-ink">Currency</label>
                    <Input
                      value={editProjectForm.currency}
                      onChange={(e) => setEditProjectForm({ ...editProjectForm, currency: e.target.value })}
                      placeholder="PKR / USD"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              )}

              <div className="pt-2 flex justify-end space-x-2 border-t border-slate-100 dark:border-brand-border/40">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditProjectModal(false)}
                  className="text-xs h-8"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-brand text-white hover:bg-brand-hover text-xs h-8 font-semibold"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Add Task */}
      {showAddTaskModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white dark:bg-[#12211F] rounded-2xl max-w-lg w-full p-6 shadow-xl border border-slate-200 dark:border-brand-border space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-brand-border/40">
              <h3 className="font-bold text-base text-brand-navy dark:text-brand-ink flex items-center space-x-2">
                <Plus className="w-4 h-4 text-brand-teal" />
                <span>Add Task to {project.name}</span>
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddTaskModal(false)}
                className="h-7 w-7 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <form onSubmit={handleCreateTask} className="space-y-3.5 text-xs">
              <div className="space-y-1">
                <label className="font-semibold text-slate-700 dark:text-brand-ink">Task Title *</label>
                <Input
                  required
                  placeholder="e.g. Implement webhook signature verification"
                  value={newTaskForm.title}
                  onChange={(e) => setNewTaskForm({ ...newTaskForm, title: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-slate-700 dark:text-brand-ink">Description</label>
                <textarea
                  rows={2}
                  placeholder="Acceptance criteria and deliverables..."
                  value={newTaskForm.description}
                  onChange={(e) => setNewTaskForm({ ...newTaskForm, description: e.target.value })}
                  className="w-full text-xs p-2 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-brand-navy dark:text-brand-ink"
                />
                <TOSComplianceGuard
                  text={newTaskForm.description || ''}
                  platform={project?.platform}
                  onCleanText={(cleaned) => setNewTaskForm({ ...newTaskForm, description: cleaned })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Assignee</label>
                  <select
                    value={newTaskForm.assignee_id}
                    onChange={(e) => setNewTaskForm({ ...newTaskForm, assignee_id: e.target.value })}
                    className="w-full h-8 px-2 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-xs text-brand-navy dark:text-brand-ink"
                  >
                    <option value="">Unassigned</option>
                    {teamMembers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.role.replace('_', ' ')})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Priority</label>
                  <select
                    value={newTaskForm.priority}
                    onChange={(e) => setNewTaskForm({ ...newTaskForm, priority: e.target.value as TaskPriority })}
                    className="w-full h-8 px-2 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-xs text-brand-navy dark:text-brand-ink"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Milestone Deliverable</label>
                  <select
                    value={newTaskForm.milestone_id}
                    onChange={(e) => setNewTaskForm({ ...newTaskForm, milestone_id: e.target.value })}
                    className="w-full h-8 px-2 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-xs text-brand-navy dark:text-brand-ink"
                  >
                    <option value="">None (Independent Task)</option>
                    {sortedMilestones.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Due Date</label>
                  <Input
                    type="date"
                    value={newTaskForm.due_date}
                    onChange={(e) => setNewTaskForm({ ...newTaskForm, due_date: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              <div className="pt-2 flex justify-end space-x-2 border-t border-slate-100 dark:border-brand-border/40">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddTaskModal(false)}
                  className="text-xs h-8"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-brand text-white hover:bg-brand-hover text-xs h-8 font-semibold"
                >
                  Create Task
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Add / Edit Milestone */}
      {(showAddMilestoneModal || editingMilestone) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white dark:bg-[#12211F] rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-200 dark:border-brand-border space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-brand-border/40">
              <h3 className="font-bold text-base text-brand-navy dark:text-brand-ink flex items-center space-x-2">
                <Flag className="w-4 h-4 text-brand-teal" />
                <span>{editingMilestone ? 'Edit Milestone' : 'Add Project Milestone'}</span>
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAddMilestoneModal(false);
                  setEditingMilestone(null);
                }}
                className="h-7 w-7 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <form
              onSubmit={editingMilestone ? handleSaveEditMilestone : handleCreateMilestone}
              className="space-y-3.5 text-xs"
            >
              <div className="space-y-1">
                <label className="font-semibold text-slate-700 dark:text-brand-ink">Milestone Title *</label>
                <Input
                  required
                  placeholder="e.g. Alpha Release & Stripe Integration"
                  value={milestoneForm.title}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-slate-700 dark:text-brand-ink">Description</label>
                <textarea
                  rows={2}
                  placeholder="Deliverable requirements and client expectations..."
                  value={milestoneForm.description}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
                  className="w-full text-xs p-2 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-brand-navy dark:text-brand-ink"
                />
                <TOSComplianceGuard
                  text={milestoneForm.description || ''}
                  platform={project?.platform}
                  onCleanText={(cleaned) => setMilestoneForm({ ...milestoneForm, description: cleaned })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Due Date</label>
                  <Input
                    type="date"
                    value={milestoneForm.due_date}
                    onChange={(e) => setMilestoneForm({ ...milestoneForm, due_date: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-brand-ink">Initial Status</label>
                  <select
                    value={milestoneForm.status}
                    onChange={(e) => setMilestoneForm({ ...milestoneForm, status: e.target.value as MilestoneStatus })}
                    className="w-full h-8 px-2 rounded-md border border-slate-300 dark:border-brand-border bg-white dark:bg-[#12211F] text-xs text-brand-navy dark:text-brand-ink"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="pt-2 flex justify-end space-x-2 border-t border-slate-100 dark:border-brand-border/40">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddMilestoneModal(false);
                    setEditingMilestone(null);
                  }}
                  className="text-xs h-8"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-brand-teal text-white hover:bg-brand-teal/90 text-xs h-8 font-semibold"
                >
                  {editingMilestone ? 'Save Milestone' : 'Add Milestone'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Project Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteProjectModal}
        itemType="Project"
        itemName={project.name}
        description={`Are you sure you want to delete project "${project.name}"? This action cannot be undone and will permanently remove all associated tasks, milestone deliverables, and audit log links.`}
        confirmLabel="Delete Project"
        onConfirm={handleDeleteProject}
        onClose={() => setShowDeleteProjectModal(false)}
      />

      {/* Delete Milestone Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={Boolean(deleteMilestoneTarget)}
        itemType="Milestone"
        itemName={deleteMilestoneTarget?.title}
        description={`Are you sure you want to delete milestone "${deleteMilestoneTarget?.title}"? Linked tasks survive and are NEVER deleted — their milestone association will simply be set to unlinked (no milestone).`}
        confirmLabel="Delete Milestone"
        onConfirm={handleDeleteMilestone}
        onClose={() => setDeleteMilestoneTarget(null)}
      />

      {/* Multi-Channel Communication & Handover Modal */}
      {showDeliveryModal && project && (
        <ChannelDeliveryModal
          isOpen={showDeliveryModal}
          onClose={() => setShowDeliveryModal(false)}
          project={project}
          milestones={milestones}
          clientPhone={(project as any).client_phone}
          clientEmail={(project as any).client_email}
          defaultChannel={project.platform as any}
        />
      )}
    </div>
  );
}
